import { exists } from '../../functions/exists';
import { Asset } from '../image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../image/dto/media-manager-folder-dto';
import { ContainerNode } from '../shared/media-manager-tree/container-node';
import { LeafNode } from '../shared/media-manager-tree/leaf-node';
import { Tree } from '../shared/media-manager-tree/tree';
import { mediaManagerSpecialTopLevelFolderIds } from './media-manager-special-folder';
import { environment } from '../../../environments/environment';

interface MediaManagerRootContainer {
  id: 'ROOT';
  type: 'root';
  name: 'Root';
}

interface MediaManagerFolderContainer {
  type: 'folder';
  folder: MediaManagerFolderDto;
}

type MediaManagerContainer = MediaManagerFolderContainer | MediaManagerRootContainer;
type MediaManagerAsset = Asset;
type MediaManagerContainerNode = ContainerNode<MediaManagerContainer, MediaManagerAsset>;
type MediaManagerFolderPathByNode = WeakMap<MediaManagerContainerNode, MediaManagerFolderDto[]>;

export interface MediaManagerSpecialFolderContext {
  isSpecialTopLevelFolder: boolean;
  isWithinSpecialFolder: boolean;
}

export interface MediaManagerAssetViewItem {
  id: string;
  asset: Asset;
  specialFolderContext: MediaManagerSpecialFolderContext;
  /**
   * Rendered as a greyed-out tile with a Disabled pill. Only a virtual section can set it: what
   * disabled means belongs to the host's collection, not to the filesystem, which has no such state.
   */
  disabled: boolean;
  getId: () => string;
}

export const emptySpecialFolderContext: MediaManagerSpecialFolderContext = {
  isSpecialTopLevelFolder: false,
  isWithinSpecialFolder: false
};

export function toAssetViewItem(
  asset: Asset,
  specialFolderContext: MediaManagerSpecialFolderContext = emptySpecialFolderContext,
  disabled: boolean = false
): MediaManagerAssetViewItem {
  const id = asset?.getUniqueIdentifier();
  return {
    id,
    asset,
    specialFolderContext: { ...specialFolderContext },
    disabled,
    getId: () => id
  };
}

export interface MediaManagerFolderTreeNode {
  id: string;
  name: string;
  // null folder means the actual filesystem root.
  folder: MediaManagerFolderDto | null;
  assets: MediaManagerAssetViewItem[];
  childFolders: MediaManagerFolderTreeNode[];
  // refers to the node's parent in the full filesystem tree.
  parentFolderId: string | null;
  path: MediaManagerFolderDto[];
  depth: number;
  // isViewRoot true means node is the root of the current projection
  // not necessarily the actual filesystem root.
  isViewRoot: boolean;
  specialFolderContext: MediaManagerSpecialFolderContext;
}

export class MediaManagerFileSystem {
  constructor(
    assetLibrary: MediaManagerAssetLibraryDto,
    private activeSpecialTopLevelFolderIds: ReadonlySet<string> = mediaManagerSpecialTopLevelFolderIds
  ) {
    this.debugLogFilesystemBuildStarted();
    this.rootNode = new ContainerNode<MediaManagerContainer, MediaManagerAsset>({
      type: 'root',
      id: 'ROOT',
      name: 'Root'
    });
    this.fileSystemTree = new Tree<MediaManagerContainer, MediaManagerAsset>(
      this.rootNode,
      this.getContainerNodeId,
      this.getLeafNodeId,
      this.debugPrintFolderData,
      this.debugPrintLeafData
    );
    this.buildFilesystem(assetLibrary);
  }

  private readonly fileSystemTree: Tree<MediaManagerContainer, MediaManagerAsset>;
  private readonly rootNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>;

  private buildFilesystem(assetLibrary: MediaManagerAssetLibraryDto) {
    this.normalizeAssetFolderIds(assetLibrary.assetFolders);
    const validAssetFolders = this.filterValidAssetFolders(assetLibrary.assetFolders);
    const folderNodeById = this.buildFolderNodeMap(validAssetFolders);
    const invalidFolderReasonById = new Map<string, string>();
    const childFoldersByParentId = this.buildChildFoldersByParentId(validAssetFolders, invalidFolderReasonById);
    const validAssets = this.filterValidAssets(assetLibrary.assets);
    const assetNodeById = this.buildAssetNodeMap(validAssets);
    this.insertFolderNodesByParentOrder(folderNodeById, childFoldersByParentId, invalidFolderReasonById);
    this.attachAssetNodesToTheirParent(validAssets, assetNodeById, invalidFolderReasonById);
  }

  generateTreeView(): MediaManagerFolderTreeNode {
    return this.projectFolderTree(this.rootNode);
  }

  getFolderView(forContainerId: string): MediaManagerFolderTreeNode | null {
    const folderNode = this.fileSystemTree.findContainerNodeById(forContainerId);
    if (folderNode) return this.projectFolderTree(folderNode);
    return null;
  }

  createFolder(folder: MediaManagerFolderDto): boolean {
    folder.id = folder?.id?.trim() ?? '';
    if (!folder.id || this.folderExists(folder.id)) return false;
    folder.parentFolderId = this.normalizeParentFolderId(folder.parentFolderId);
    const parentNode = this.fileSystemTree.findContainerNodeById(folder.parentFolderId);
    if (!parentNode) return false;
    return this.fileSystemTree.insertNode(
      new ContainerNode<MediaManagerContainer, MediaManagerAsset>({
        type: 'folder',
        folder: folder
      }),
      parentNode
    );
  }

  createAsset(asset: Asset): boolean {
    if (!asset?.isValid()) return false;
    const assetId = asset?.getUniqueIdentifier();
    if (!assetId || this.assetExists(assetId)) return false;
    asset.parentFolderId = this.normalizeParentFolderId(asset.parentFolderId);
    const parentNode = this.fileSystemTree.findContainerNodeById(asset.parentFolderId);
    if (!parentNode) return false;
    return this.fileSystemTree.insertNode(new LeafNode<MediaManagerContainer, MediaManagerAsset>(asset), parentNode);
  }

  moveAsset(oldAssetId: string, updatedAsset: Asset): boolean {
    const currentNode = this.fileSystemTree.findLeafNodeById(oldAssetId);
    const targetParentFolderId = this.normalizeParentFolderId(updatedAsset.parentFolderId);
    const targetParentNode = this.fileSystemTree.findContainerNodeById(targetParentFolderId);
    if (currentNode && targetParentNode && this.fileSystemTree.moveNode(currentNode, targetParentNode)) {
      updatedAsset.parentFolderId = targetParentFolderId;
      currentNode.data.parentFolderId = targetParentFolderId;
      return true;
    }
    return false;
  }

  moveFolder(folderId: string, targetParentFolderId: string): boolean {
    const currentNode = this.fileSystemTree.findContainerNodeById(folderId);
    const normalizedTargetParentFolderId = this.normalizeParentFolderId(targetParentFolderId);
    const targetParentNode = this.fileSystemTree.findContainerNodeById(normalizedTargetParentFolderId);
    if (currentNode && targetParentNode && this.fileSystemTree.moveNode(currentNode, targetParentNode)) {
      if (this.isFolderContainer(currentNode.data)) {
        currentNode.data.folder.parentFolderId = normalizedTargetParentFolderId;
      }
      return true;
    }
    return false;
  }

  patchAssetMetadata(oldAssetId: string, updatedAsset: Asset): boolean {
    const existingAssetNode = this.fileSystemTree.findLeafNodeById(oldAssetId);
    if (!existingAssetNode) return false;
    existingAssetNode.data.displayName = updatedAsset.displayName;
    existingAssetNode.data.fileName = updatedAsset.fileName;
    existingAssetNode.data.assetLibraryType = updatedAsset.assetLibraryType;
    existingAssetNode.data.mediaClass = updatedAsset.mediaClass;
    existingAssetNode.data.mediaType = updatedAsset.mediaType;
    existingAssetNode.data.tags = updatedAsset.tags;
    return true;
  }

  patchFolderMetadata(folderId: string, updatedFolder: MediaManagerFolderDto): boolean {
    const existingContainerNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!existingContainerNode) return false;
    if (!this.isFolderContainer(existingContainerNode.data)) return false;
    existingContainerNode.data.folder.name = updatedFolder.name;
    existingContainerNode.data.folder.lastModified = updatedFolder.lastModified;
    return true;
  }

  deleteAsset(assetId: string): boolean {
    const existingAssetNode = this.fileSystemTree.findLeafNodeById(assetId);
    if (!existingAssetNode) return false;
    return this.fileSystemTree.deleteNode(existingAssetNode);
  }

  deleteFolder(folderId: string): boolean {
    const existingFolderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!existingFolderNode || this.fileSystemTree.isRootNode(existingFolderNode)) return false;
    return this.fileSystemTree.deleteNode(existingFolderNode);
  }

  assetExists(id: string): boolean {
    return this.fileSystemTree.findLeafNodeById(id) !== null;
  }

  getAssetById(id: string): Asset | null {
    return this.fileSystemTree.findLeafNodeById(id)?.data ?? null;
  }

  folderExists(id: string): boolean {
    return this.fileSystemTree.findContainerNodeById(id) !== null;
  }

  getFolderById(id: string): MediaManagerFolderDto | null {
    const folderNode = this.fileSystemTree.findContainerNodeById(id);
    if (!folderNode || this.fileSystemTree.isRootNode(folderNode)) return null;
    if (!this.isFolderContainer(folderNode.data)) return null;
    return folderNode.data.folder;
  }

  getFolderDepth(folderId: string): number | null {
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return null;
    return this.fileSystemTree.getDepth(folderNode);
  }

  isFolderWithinFolder(folderId: string, ancestorFolderId: string): boolean {
    if (!folderId || !ancestorFolderId || folderId === ancestorFolderId) return false;
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    const ancestorNode = this.fileSystemTree.findContainerNodeById(ancestorFolderId);
    if (!folderNode || !ancestorNode) return false;
    return this.fileSystemTree.ancestorContainsDescendantContainer(ancestorNode, folderNode);
  }

  isAssetWithinFolder(assetId: string, ancestorFolderId: string): boolean {
    if (!assetId || !ancestorFolderId) return false;
    const assetNode = this.fileSystemTree.findLeafNodeById(assetId);
    const ancestorNode = this.fileSystemTree.findContainerNodeById(ancestorFolderId);
    if (!assetNode || !ancestorNode) return false;
    return this.fileSystemTree.ancestorContainsDescendantLeaf(ancestorNode, assetNode);
  }

  folderHasAnyAncestorInSet(folderId: string, ancestorFolderIds: Set<string>): string | null {
    if (!folderId || ancestorFolderIds.size === 0) return null;
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return null;
    let currentParent = this.fileSystemTree.getParent(folderNode);
    while (currentParent) {
      const currentParentId = this.getContainerNodeId(currentParent.data);
      if (ancestorFolderIds.has(currentParentId)) {
        return currentParentId;
      }
      currentParent = this.fileSystemTree.getParent(currentParent);
    }
    return null;
  }

  assetHasAnyAncestorInSet(assetId: string, ancestorFolderIds: Set<string>): string | null {
    if (!assetId || ancestorFolderIds.size === 0) return null;
    const assetNode = this.fileSystemTree.findLeafNodeById(assetId);
    if (!assetNode) return null;
    let currentParent = this.fileSystemTree.getParent(assetNode);
    while (currentParent) {
      const currentParentId = this.getContainerNodeId(currentParent.data);
      if (ancestorFolderIds.has(currentParentId)) {
        return currentParentId;
      }
      currentParent = this.fileSystemTree.getParent(currentParent);
    }
    return null;
  }

  getDescendantFolderIds(folderId: string): string[] {
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return [];
    const descendantFolderIds: string[] = [];
    this.collectDescendantFolderIds(folderNode, descendantFolderIds);
    return descendantFolderIds;
  }

  getDescendantAssetIds(folderId: string): string[] {
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return [];
    const descendantAssetIds: string[] = [];
    this.collectDescendantAssetIds(folderNode, descendantAssetIds);
    return descendantAssetIds;
  }

  getDirectChildFolders(folderId: string): MediaManagerFolderDto[] {
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return [];
    return folderNode.children
      .filter(child => this.fileSystemTree.isContainerNode(child))
      .map(child => this.resolveProjectedContainer(child.data))
      .filter((folder): folder is MediaManagerFolderDto => this.nodeIsMediaManagerFolderDto(folder));
  }

  getDirectChildAssets(folderId: string): Asset[] {
    const folderNode = this.fileSystemTree.findContainerNodeById(folderId);
    if (!folderNode) return [];
    return folderNode.children.filter(child => this.fileSystemTree.isLeafNode(child)).map(child => child.data);
  }

  private projectFolderTree(
    startNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>
  ): MediaManagerFolderTreeNode {
    const pathByNode: MediaManagerFolderPathByNode = new WeakMap();
    return this.fileSystemTree.projectContainerSubtreeFrom(
      startNode,
      (
        curNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>,
        childFolders: MediaManagerFolderTreeNode[],
        depth: number,
        parentFolderId: string | null
      ): MediaManagerFolderTreeNode => {
        const assets = curNode.children.filter(child => this.fileSystemTree.isLeafNode(child)).map(child => child.data);
        return this.buildProjectedFolderTreeNode(curNode, childFolders, assets, depth, parentFolderId, pathByNode);
      }
    );
  }

  private buildProjectedFolderTreeNode(
    curNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>,
    childFolders: MediaManagerFolderTreeNode[],
    assets: Asset[],
    depth: number,
    parentFolderId: string | null,
    pathByNode: MediaManagerFolderPathByNode
  ): MediaManagerFolderTreeNode {
    const folder = this.resolveProjectedContainer(curNode.data);
    const path = this.getProjectedFolderPath(curNode, pathByNode);
    const specialFolderContext = this.buildSpecialFolderContext(folder, path);
    return {
      id: folder?.id ?? 'ROOT',
      name: folder?.name ?? 'Root',
      folder: folder,
      assets: assets.map(asset => this.projectAssetViewItem(asset, specialFolderContext)),
      childFolders: childFolders,
      parentFolderId: parentFolderId,
      path: path,
      depth: depth,
      isViewRoot: depth === 0,
      specialFolderContext
    };
  }

  private getProjectedFolderPath(
    curNode: MediaManagerContainerNode,
    pathByNode: MediaManagerFolderPathByNode
  ): MediaManagerFolderDto[] {
    const cachedPath = pathByNode.get(curNode);
    if (cachedPath) return cachedPath;
    const parent = this.fileSystemTree.getParent(curNode);
    const parentPath = parent ? this.getProjectedFolderPath(parent, pathByNode) : [];
    const folder = this.resolveProjectedContainer(curNode.data);
    const path = folder ? [...parentPath, folder] : parentPath;
    pathByNode.set(curNode, path);
    return path;
  }

  private buildSpecialFolderContext(
    folder: MediaManagerFolderDto | null,
    path: MediaManagerFolderDto[]
  ): MediaManagerSpecialFolderContext {
    const specialTopLevelFolder =
      (folder && this.isSpecialTopLevelFolderId(folder.id) ? folder : null) ??
      path.find(pathFolder => this.isSpecialTopLevelFolderId(pathFolder.id)) ??
      null;
    return {
      isSpecialTopLevelFolder: exists(folder) && this.isSpecialTopLevelFolderId(folder.id),
      isWithinSpecialFolder: exists(specialTopLevelFolder)
    };
  }

  private projectAssetViewItem(
    asset: Asset,
    specialFolderContext: MediaManagerSpecialFolderContext
  ): MediaManagerAssetViewItem {
    return toAssetViewItem(asset, specialFolderContext);
  }

  isActiveSpecialTopLevelFolderId(folderId: string | null | undefined): boolean {
    return this.isSpecialTopLevelFolderId(folderId);
  }

  private isSpecialTopLevelFolderId(folderId: string | null | undefined): boolean {
    return exists(folderId) && this.activeSpecialTopLevelFolderIds.has(folderId);
  }

  private normalizeAssetFolderIds(assetFolders: MediaManagerFolderDto[]): void {
    assetFolders.forEach(assetFolder => {
      assetFolder.id = assetFolder.id?.trim() ?? '';
      assetFolder.parentFolderId = this.normalizeParentFolderId(assetFolder.parentFolderId);
    });
  }

  private buildFolderNodeMap(assetFolders: MediaManagerFolderDto[]) {
    const folderNodeById: Map<string, ContainerNode<MediaManagerContainer, MediaManagerAsset>> = new Map();
    assetFolders.forEach(assetFolder => {
      const assetFolderNode = new ContainerNode<MediaManagerContainer, MediaManagerAsset>({
        type: 'folder',
        folder: assetFolder
      });
      if (folderNodeById.has(assetFolder.id)) {
        this.debugWarnDuplicateFolderScaffolding(assetFolder.id);
        return;
      }
      folderNodeById.set(assetFolder.id, assetFolderNode);
    });
    return folderNodeById;
  }

  private buildChildFoldersByParentId(
    assetFolders: MediaManagerFolderDto[],
    invalidFolderReasonById: Map<string, string>
  ): Map<string, MediaManagerFolderDto[]> {
    const childFoldersByParentId = new Map<string, MediaManagerFolderDto[]>();
    assetFolders.forEach(assetFolder => {
      if (assetFolder.id === assetFolder.parentFolderId) {
        invalidFolderReasonById.set(assetFolder.id, 'Folder cannot be its own parent');
        return;
      }
      const childFolders = childFoldersByParentId.get(assetFolder.parentFolderId) ?? [];
      childFolders.push(assetFolder);
      childFoldersByParentId.set(assetFolder.parentFolderId, childFolders);
    });
    return childFoldersByParentId;
  }

  // Insert folders with a BFS from ROOT so every target parent is already in the tree before its children are inserted.
  // Folders that cannot be reached from ROOT are left out and marked with reasons for debug logging and asset warnings.
  private insertFolderNodesByParentOrder(
    folderNodeById: Map<string, ContainerNode<MediaManagerContainer, MediaManagerAsset>>,
    childFoldersByParentId: Map<string, MediaManagerFolderDto[]>,
    invalidFolderReasonById: Map<string, string>
  ): void {
    const rootId = this.getContainerNodeId(this.rootNode.data);
    if (!rootId) return;
    const parentFolderIdQueue: string[] = [rootId];
    // Use an indexed queue instead of shift() to avoid repeatedly re-indexing the array.
    for (let i = 0; i < parentFolderIdQueue.length; i++) {
      const parentFolderId = parentFolderIdQueue[i];
      const parentContainerNode = this.fileSystemTree.findContainerNodeById(parentFolderId);
      if (!parentContainerNode) continue;
      const childFolders = childFoldersByParentId.get(parentFolderId) ?? [];
      childFolders.forEach(childFolder => {
        const childFolderNode = folderNodeById.get(childFolder.id);
        if (childFolderNode && this.fileSystemTree.insertNode(childFolderNode, parentContainerNode)) {
          parentFolderIdQueue.push(childFolder.id);
        }
      });
    }
    this.markUninsertedFolderReasons(folderNodeById, invalidFolderReasonById);
    this.debugWarnUninsertedFolders(folderNodeById, invalidFolderReasonById);
  }

  private attachAssetNodesToTheirParent(
    assets: Asset[],
    assetNodeById: Map<string, LeafNode<MediaManagerContainer, Asset>>,
    invalidFolderReasonById: Map<string, string>
  ) {
    assets.forEach(asset => {
      const assetNode = assetNodeById.get(asset.getUniqueIdentifier());
      asset.parentFolderId = this.normalizeParentFolderId(asset.parentFolderId);
      const parentContainerNode = this.fileSystemTree.findContainerNodeById(asset.parentFolderId);
      if (assetNode && parentContainerNode && this.fileSystemTree.insertNode(assetNode, parentContainerNode)) return;
      this.debugWarnAssetInsertFailed(asset, invalidFolderReasonById);
    });
  }

  private markUninsertedFolderReasons(
    folderNodeById: Map<string, ContainerNode<MediaManagerContainer, MediaManagerAsset>>,
    invalidFolderReasonById: Map<string, string>
  ): void {
    const resolvingFolderIds = new Set<string>();
    const rootId = this.getContainerNodeId(this.rootNode.data);
    const resolveInvalidReason = (
      folderId: string,
      folderNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>
    ): string => {
      const existingInvalidReason = invalidFolderReasonById.get(folderId);
      if (existingInvalidReason) return existingInvalidReason;
      if (this.fileSystemTree.findContainerNodeById(folderId)) return '';
      if (!this.isFolderContainer(folderNode.data)) return 'Folder container data is invalid';
      const parentFolderId = folderNode.data.folder.parentFolderId;
      const parentInvalidReason = invalidFolderReasonById.get(parentFolderId);
      if (parentInvalidReason) {
        const invalidReason = `Parent folder ${parentFolderId} is invalid, reason -> ${parentInvalidReason}`;
        invalidFolderReasonById.set(folderId, invalidReason);
        return invalidReason;
      }
      if (parentFolderId === rootId) {
        const invalidReason = 'Folder could not be inserted under ROOT';
        invalidFolderReasonById.set(folderId, invalidReason);
        return invalidReason;
      }
      const parentFolderNode = folderNodeById.get(parentFolderId);
      if (!parentFolderNode) {
        const invalidReason = `Parent folder ${JSON.stringify(parentFolderId)} does not exist`;
        invalidFolderReasonById.set(folderId, invalidReason);
        return invalidReason;
      }
      if (resolvingFolderIds.has(parentFolderId)) {
        const invalidReason = `Folder is part of a parent cycle involving ${JSON.stringify(parentFolderId)}`;
        invalidFolderReasonById.set(folderId, invalidReason);
        return invalidReason;
      }
      resolvingFolderIds.add(folderId);
      const resolvedParentInvalidReason = resolveInvalidReason(parentFolderId, parentFolderNode);
      resolvingFolderIds.delete(folderId);
      const invalidReason = resolvedParentInvalidReason
        ? `Parent folder ${JSON.stringify(parentFolderId)} is invalid, reason -> ${resolvedParentInvalidReason}`
        : 'Folder is not reachable from ROOT';
      invalidFolderReasonById.set(folderId, invalidReason);
      return invalidReason;
    };
    folderNodeById.forEach((folderNode, folderId) => {
      if (this.fileSystemTree.findContainerNodeById(folderId)) return;
      resolveInvalidReason(folderId, folderNode);
    });
  }

  private filterValidAssets(assets: Asset[]): Asset[] {
    return assets.filter(asset => {
      if (asset?.isValid?.()) return true;
      this.debugWarnInvalidAssetScaffolding(asset);
      return false;
    });
  }

  private filterValidAssetFolders(folders: MediaManagerFolderDto[]): MediaManagerFolderDto[] {
    return folders.filter(folder => {
      if (folder.id) return true;
      this.debugWarnInvalidAssetFolderScaffolding(folder);
      return false;
    });
  }

  private buildAssetNodeMap(assetEntries: Asset[]) {
    const assetNodeById: Map<string, LeafNode<MediaManagerContainer, MediaManagerAsset>> = new Map();
    assetEntries.forEach(asset => {
      const assetId = asset.getUniqueIdentifier();
      const assetNode = new LeafNode<MediaManagerContainer, MediaManagerAsset>(asset);
      if (assetNodeById.has(assetId)) {
        this.debugWarnDuplicateAssetScaffolding(assetId);
        return;
      }
      assetNodeById.set(assetId, assetNode);
    });
    return assetNodeById;
  }

  private normalizeParentFolderId(parentFolderId: string | null | undefined): string {
    return parentFolderId?.trim() || this.getContainerNodeId(this.rootNode.data);
  }

  private nodeIsMediaManagerFolderDto(node: MediaManagerFolderDto | null): node is MediaManagerFolderDto {
    return node !== null;
  }

  private resolveProjectedContainer(container: MediaManagerContainer) {
    if (this.isFolderContainer(container)) return container.folder;
    // implies container is root
    return null;
  }

  private collectDescendantFolderIds(
    currentNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>,
    descendantFolderIds: string[]
  ): void {
    const childFolderNodes = currentNode.children.filter(child => this.fileSystemTree.isContainerNode(child)) as Array<
      ContainerNode<MediaManagerContainer, MediaManagerAsset>
    >;
    for (const childFolderNode of childFolderNodes) {
      descendantFolderIds.push(this.getContainerNodeId(childFolderNode.data));
      this.collectDescendantFolderIds(childFolderNode, descendantFolderIds);
    }
  }

  private collectDescendantAssetIds(
    currentNode: ContainerNode<MediaManagerContainer, MediaManagerAsset>,
    descendantAssetIds: string[]
  ): void {
    for (const childNode of currentNode.children) {
      if (this.fileSystemTree.isLeafNode(childNode)) {
        descendantAssetIds.push(this.getLeafNodeId(childNode.data));
        continue;
      }

      this.collectDescendantAssetIds(
        childNode as ContainerNode<MediaManagerContainer, MediaManagerAsset>,
        descendantAssetIds
      );
    }
  }

  private getContainerNodeId = (node: MediaManagerContainer): string => {
    if (this.isFolderContainer(node)) return node.folder.id;
    return node.id;
  };
  private getLeafNodeId = (node: MediaManagerAsset): string => {
    return node.getUniqueIdentifier();
  };

  private isFolderContainer = (node: MediaManagerContainer): node is MediaManagerFolderContainer => {
    return node.type === 'folder';
  };

  public debugFormatFileSystemTree(): string {
    return this.fileSystemTree.debugPrintPrettyString();
  }

  public debugPrintFileSystemTree(): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.info(`---- FILESYSTEM TREE ----\n${this.debugFormatFileSystemTree()}`);
  }

  private debugFormatFolderTreeView(startNode: MediaManagerFolderTreeNode): string {
    const lines: string[] = [];
    const dfs = (node: MediaManagerFolderTreeNode, prefix: string, isLast: boolean) => {
      let connector = '';
      if (!node.isViewRoot) {
        connector = isLast ? '└── ' : '├── ';
      }
      const metadata = [
        `depth=${node.depth}`,
        `assets=${node.assets.length}`,
        `parent=${node.parentFolderId ?? 'null'}`,
        `viewRoot=${node.isViewRoot}`
      ].join(', ');
      lines.push(`${prefix}${connector}${node.name} (${metadata})`);
      const assetPrefix = node.isViewRoot ? prefix : prefix + (isLast ? '    ' : '│   ');
      node.assets.forEach(assetViewItem => {
        lines.push(`${assetPrefix}• ${assetViewItem.asset.fileName}`);
      });
      const lastChildIndex = node.childFolders.length - 1;
      node.childFolders.forEach((childFolder, index) => {
        const childIsLast = index === lastChildIndex;
        dfs(childFolder, assetPrefix, childIsLast);
      });
    };
    dfs(startNode, '', true);
    return lines.join('\n');
  }

  public debugPrintFullFolderTreeView(treeView: MediaManagerFolderTreeNode | null | undefined): void {
    this.debugLogFullFolderTreeView(treeView);
  }

  public debugPrintFolderSubTreeView(
    folderId: string,
    subTreeView: MediaManagerFolderTreeNode | null | undefined
  ): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    if (!subTreeView) {
      console.warn(
        `MediaManager could not print folder sub view for ${folderId} because no projected subtree was provided.`
      );
      return;
    }
    console.info(
      `---- FOLDER VIEW SUB TREE with folder ID '${folderId}' ----\n${this.debugFormatFolderTreeView(subTreeView)}`
    );
  }

  private debugLogFilesystemBuildStarted(): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.info(`Building filesystem from AssetLibrary...`);
  }

  private debugWarnDuplicateFolderScaffolding(folderId: string): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.warn(`MediaManager folder scaffolding already contained ${JSON.stringify(folderId)}`);
  }

  private debugWarnAssetInsertFailed(asset: Asset, invalidFolderReasonById: Map<string, string>): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    const invalidParentReason = invalidFolderReasonById.get(asset.parentFolderId);
    const parentFailureReason = invalidParentReason
      ? `Parent folder ${JSON.stringify(asset.parentFolderId)} is invalid, reason -> ${invalidParentReason}.`
      : `Parent folder ${JSON.stringify(asset.parentFolderId)} does not exist in the filesystem tree.`;
    console.warn(
      `MediaManager could not insert asset uniqueId: ${asset.getUniqueIdentifier()}, reason -> ${parentFailureReason}`
    );
  }

  private debugWarnUninsertedFolders(
    folderNodeById: Map<string, ContainerNode<MediaManagerContainer, MediaManagerAsset>>,
    invalidFolderReasonById: Map<string, string>
  ): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    folderNodeById.forEach((folderNode, folderId) => {
      if (this.fileSystemTree.findContainerNodeById(folderId)) return;
      const invalidReason = invalidFolderReasonById.get(folderId);
      console.warn(
        `MediaManager could not insert assetFolder ${JSON.stringify(folderNode.data)}. ` +
          `${invalidReason ?? 'The assetFolder or parent container could not be added to the filesystem tree.'}`
      );
    });
  }

  private debugWarnDuplicateAssetScaffolding(assetId: string): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.warn(`MediaManager asset scaffolding already contained ${JSON.stringify(assetId)}`);
  }

  private debugWarnInvalidAssetScaffolding(asset: Asset): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.warn(`MediaManager asset scaffolding skipped invalid asset ${JSON.stringify(asset)}`);
  }

  private debugWarnInvalidAssetFolderScaffolding(assetFolder: MediaManagerFolderDto): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    console.warn(`MediaManager assetFolder scaffolding skipped invalid assetFolder id: ${assetFolder.id}`);
  }
  private debugLogFullFolderTreeView(treeView: MediaManagerFolderTreeNode | null | undefined): void {
    if (!this.shouldPrintFilesystemDebugLogs()) return;
    if (!treeView) {
      console.warn(`MediaManager could not print full folder view tree because no projected tree was provided.`);
      return;
    }
    console.info(`---- FOLDER VIEW ENTIRE TREE ----\n${this.debugFormatFolderTreeView(treeView)}`);
  }

  private debugPrintFolderData = (node: MediaManagerContainer) => {
    if (this.isFolderContainer(node)) return node.folder.name;
    return (node as MediaManagerRootContainer).name;
  };

  private debugPrintLeafData = (node: MediaManagerAsset): string => {
    return node.fileName;
  };

  private shouldPrintFilesystemDebugLogs(): boolean {
    return !environment.production;
  }
}
